<template>
  <div
    class="draft-list-create-wrap"
    :style="draftListCreateStyle"
    :data-selected-card="$store.getters.selectedCardId === 'new-list'"
    :data-list-id="'draft-list-create'"
  >
    <div
      v-if="!draftListCreateView && !unpublishedBoard && !inEpicBoard"
      v-draghover="'create-add-button__list_dragging-on'"
      class="create-add-button pt usn"
      @click="createDraftList"
    >
      <em
        class="board-list-icon-add"
        :style="getCustomIconStyle(iconPath('plus-circle'))"
      />
      <span>{{ translate('createNewList') }}</span>
    </div>
    <div
      v-if="draftListCreateView"
      v-click-outside="handleOutsideClick"
      class="new-list-input-wrap"
      @keydown.esc.stop="hideCreateView"
      @mouseup="blurElementHandler"
      @click.stop
    >
      <div class="new-list-input-field">
        <em
          class="status-icon pt"
          :style="{
            ...getCustomIconStyle(iconPath(listStatus.icon || 'circle-dotted')),
            background: listStatus.color || styleVariables.gray400,
          }"
          @click="showDropdown = true"
        />
        <ListCardStatusPicker
          v-if="showDropdown"
          :key="localId"
          ref="statusPicker"
          :selected-status="listStatus"
          :keyboard-navigation="false"
          :custom-statuses-list="searchResults"
          :draft-list-create="true"
          class="absolute"
          :class="{
            'top-10': !isLayoutList && !isLayoutTimeline,
          }"
          @click="handleClick"
          @colorSelect="handleColorSelect"
          @setHighlighted="setHighlighted"
          @esc="handleEsc"
          @click-outside="handleStatusClickOutside"
        />
        <input
          ref="draftListCreateInput"
          v-model="draftListTitle"
          class="new-list-name-input"
          type="text"
          :placeholder="translate('enterListName')"
          data-hidden-input="true"
          @keypress.enter.exact="publishDraftList"
          @keydown.stop="keyDownHandler"
          @mousedown="focusElementHandler"
          @mouseup.stop="blurElementHandler"
        />
      </div>
      <div class="new-list-add-button">
        <ButtonCustom
          :type="'important-custom'"
          :variation="'extra-small'"
          class="pt usn bg-important-bg hover:bg-important-hover border-important-border"
          :custom-icon-name="'x'"
          :no-label="true"
          @click.stop="draftListCreateView = false"
        />
        <ButtonCustom
          :variation="'extra-small'"
          :type="'important-custom'"
          :custom-icon-name="'check'"
          class="pt usn second bg-important-bg hover:bg-important-hover border-important-border"
          :no-label="true"
          @click="checkClickHandler"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { nanoid } from 'nanoid';
import ButtonCustom from '@/components/elements/ButtonCustom';
import customIconsMixin from '@/mixins/customIconsMixin';
import ListCardStatusPicker from '@/components/widgets/ListCardStatusPicker';
import cardStatuses from '@/mixins/cardStatuses';
import styleVariables from '@/assets/scss/variables.module.scss';
import elementFocusMixin from '@/mixins/elementFocusMixin';
import EventBus from '@/utilities/eventBus';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import cardStatusType from '@/types/cardStatusType';

export default {
  name: 'DraftListCreate',
  components: {
    ButtonCustom,
    ListCardStatusPicker,
  },

  mixins: [customIconsMixin, cardStatuses, elementFocusMixin],

  emits: ['scrollToEnd', 'horizontalScrollToEnd'],

  setup() {
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();

    return {
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
    };
  },

  data() {
    return {
      showDropdown: true,
      draftListCreateView: false,
      draftListTitle: '',
      listStatus: {
        color: styleVariables.gray400,
        id: 'backlog',
        label: 'Backlog',
        icon: 'circle-dotted',
      },
      filteredOutKeyCodes: ['Space', 'Tab'],
      // selected index for status picker dropdown
      // used because index from child component(status dropdown) cannn't be used after closing dropdown
      selectedItem: 0,
      localId: `ll-${nanoid(6)}`,
    };
  },
  computed: {
    draftListInputVisibleOnOpen() {
      return this.$store.getters.draftListInputVisibleOnOpen;
    },
    unpublishedBoard() {
      return !this.currentBoardId;
    },
    currentBoardId() {
      return this.embed ? this.embeddedBoardAttributes.boardId : this.$store.getters.currentBoardId;
    },

    searchResults() {
      const isEpic = this.$route.name === this.$constants.epicsBoardRoute.name;
      return this.defaultLists(isEpic);
    },

    items() {
      return this.searchResults;
    },

    cardsLayout() {
      return this.embed
        ? this.embeddedBoardAttributes.layout
        : this.$store.getters.cardsLayout(this.currentBoardId);
    },

    isLayoutList() {
      return this.cardsLayout === 'list';
    },

    isLayoutTimeline() {
      return this.cardsLayout === 'timeline';
    },

    timelineSidebarWidth() {
      return this.$store.getters.getTimelineSidebarWidth;
    },

    draftListCreateStyle() {
      return {
        ...(this.isLayoutTimeline && { maxWidth: `${this.timelineSidebarWidth - 1}px !important` }),
      };
    },
  },
  watch: {
    draftListInputVisibleOnOpen(newVal) {
      if (newVal) {
        this.createDraftList();
      }
    },

    draftListCreateView(newVal) {
      if (newVal) {
        this.setDefaultStatus();
        if (this.$refs.statusPicker) {
          this.$refs.statusPicker.resetSelectedIndex();
        }
      }
    },
  },
  mounted() {
    this.selectedIndex = 0;
    if (this.draftListInputVisibleOnOpen) {
      this.createDraftList();
    }
    EventBus.$on('addListButton:click', this.addListMessageHandler);
  },
  unmounted() {
    EventBus.$off('addListButton:click', this.addListMessageHandler);
  },
  methods: {
    addListMessageHandler() {
      this.createDraftList();
    },
    keyDownHandler(e) {
      /*
      to prevent using mixin both, parant and child component at the same time
      we are using refs to call mixins methods in child component
    */
      this.$refs.statusPicker?.checkDownKey(e);
      if (this.showDropdown) {
        this.$refs.statusPicker?.checkKey(e);
      }
    },
    closeAddCardIfOpen() {
      if (this.$store.getters.getListDraftCardCreateView) {
        this.$store.dispatch('setListDraftCardCreateView', '');
      }
    },
    createDraftList() {
      this.showDropdown = true;
      this.closeAddCardIfOpen();
      if (this.unpublishedBoard) {
        return;
      }
      this.draftListCreateView = true;
      this.$emit('scrollToEnd');
      this.focusInput();

      this.$store.dispatch('setDraftListInputVisibleOnOpen', false);
    },
    localAddList(localId) {
      this.$store.dispatch('setBoardListsFlat', [
        {
          id: localId,
          title: this.draftListTitle,
          localId,
          board_id: this.currentBoardId,
          behavior: this.listStatus.id,
          icon: this.listStatus.icon,
          color: this.listStatus.color,
        },
      ]);
      this.$store.dispatch('addBoardListId', {
        boardId: this.currentBoardId,
        listId: localId,
        ...(this.isLayoutTimeline && { position: 0 }),
      });
      this.localId = `ll-${nanoid(6)}`;
    },
    checkClickHandler() {
      this.publishDraftList(true, true);
    },
    publishDraftList(openNewInput = true, discardSelectionCheck = false) {
      const selectedStatus = this.searchResults[this.selectedIndex];

      if (selectedStatus && this.listStatus.id !== selectedStatus.id && !discardSelectionCheck) {
        return;
      }
      if (!this.draftListTitle || !this.draftListTitle.trim()) {
        this.focusInput();
        return;
      }

      // store localId before localAddList so in catch we use it to remove list from local store
      const localId = this.localId;

      this.localAddList(this.localId);

      this.$store
        .dispatch('createBoardList', {
          boardId: this.currentBoardId,
          list: {
            title: this.draftListTitle,
            behavior: this.listStatus.id,
            icon: this.listStatus.icon,
            color: this.listStatus.color,
          },
        })
        .then(({ list }) => {
          this.showDropdown = true;
          this.setDefaultStatus();
          this.$store.dispatch('setBoardListsFlat', [list]);
          this.$store.dispatch('removeBoardList', localId);
          this.$store.dispatch('replaceBoardListId', {
            boardId: this.currentBoardId,
            localId,
            listId: list.id,
          });
          this.$store.dispatch('setSingleListInListCards', {
            listId: list.id,
            value: [],
          });
          this.$store.dispatch('lastHoveredListId', list.id);
          if (this.isLayoutTimeline) {
            this.$store.dispatch('updateList', {
              boardId: list.board_id,
              listId: list.id,
              list: { position: 0 },
            });
          }
        })
        .catch((/* err */) => {
          this.$store.dispatch('removeBoardListId', {
            boardId: this.currentBoardId,
            id: localId,
          });

          this.$store.dispatch('removeBoardList', localId);
          /*
            Display notification enabling cancel and retry action.
            - cancel action removes list from local store
          */
        });

      this.draftListTitle = '';
      this.draftListCreateView = false;
      this.$nextTick(() => {
        this.$emit('scrollToEnd');
      });
      if (openNewInput) {
        this.localId = `ll-${nanoid(6)}`;
        this.setDefaultStatus();
        this.createDraftList();
      }
    },
    focusInput() {
      this.$nextTick(() => {
        this.$refs.draftListCreateInput.select();
        this.$emit('horizontalScrollToEnd');
      });
    },
    hideCreateView() {
      this.draftListCreateView = false;
    },

    handleClick(status, index) {
      this.listStatus = status;
      this.showDropdown = false;
      this.selectedIndex = index;
      this.focusInput();
    },

    handleColorSelect(color) {
      this.listStatus.color = color;
    },

    setDefaultStatus() {
      const isEpic = this.currentBoardId === this.$constants.epicsBoardId;
      this.listStatus = { ...getCardStatus(cardStatusType.Backlog, isEpic) };
      this.$nextTick(() => {
        this.selectedIndex = 0;
      });
    },

    handleOutsideClick() {
      if (this.$refs.statusPicker) {
        this.selectedIndex = this.$refs.statusPicker.selectedIndex;
      }
      if (this.isFocusElement()) return;
      if (this.draftListTitle === '') {
        this.draftListCreateView = false;
        this.$store.dispatch('setDraftListInputVisibleOnOpen', false);
        return;
      }
      this.publishDraftList(false);
    },

    selectSearchResult(status) {
      this.handleClick(status);
    },

    setHighlighted(value) {
      this.selectedIndex = value;
    },

    handleEsc() {
      this.hideCreateView();
    },

    handleStatusClickOutside() {
      if (this.selectedIndex === 0) this.showDropdown = false;
    },
  },
};
</script>

<style lang="scss">
$newListInputPaddingRight: 8px;
$statusIconDimension: 16px;
$newListElementHeight: 42px;
$columnLeftRightPadding: $boardColumnPaddingLeft + $boardColumnPaddingRight;

.draft-list-create-wrap {
  padding: 0 $boardColumnPaddingRight 0px $boardColumnPaddingLeft;
  box-sizing: border-box;
  align-self: flex-start;

  &.grid-view-draft-list {
    margin-top: 0;
    margin-bottom: 48px;
    margin-left: 10px;
  }
  .create-add-button {
    width: $columnWidth;
    height: $newListElementHeight;
    padding: 11px 8px 11px 7px;
    border: 1px solid transparent;
    border-radius: 8px;
    display: flex;
    align-items: center;
    background-color: transparent;
    box-sizing: border-box;

    &.grid-view-add-button {
      width: calc(100% - 30px);
    }

    .board-list-icon-add {
      width: 16px;
      height: 16px;
      margin-right: 8px;
      @apply bg-icon-default;
    }
    em {
      font-size: 16px;
      color: #929292;
    }
    span {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      @apply text-text-default;
    }
    &:hover {
      @apply bg-hover-default;
    }

    &__list_dragging-on {
      cursor: default !important;
      &:hover {
        @apply bg-transparent;
      }
    }
  }

  .new-list-input-wrap {
    height: $newListElementHeight;
    width: $columnWidth;
    outline: none;
    background-color: #fff;
    display: flex;
    box-shadow: 0 0 0 1px $stLightSilver inset;
    border-radius: 8px;
    padding: 11px $newListInputPaddingRight 11px 8px;
    align-items: center;
    box-sizing: border-box;
    justify-content: space-between;
    @apply bg-important-bg text-text-default;

    .new-list-input-field {
      position: relative;
      display: flex;
      align-items: center;

      .new-list-name-input {
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        border: none;

        outline: none;
        width: calc(100% - 24px);
        background-color: transparent;
        @apply text-text-default;

        &::placeholder {
          @apply text-text-placeholder;
        }
      }
      .board-list-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        color: #ffffff;
        cursor: pointer;
        user-select: none;
        margin: 0 5px;
        &.default-icon {
          font-size: 24px;
          background: #454545;
        }
      }

      .status-icon {
        width: $statusIconDimension;
        height: $statusIconDimension;
        margin-right: 8px;
      }
    }

    .new-list-add-button {
      display: flex;
      align-items: center;
      height: 100%;
      top: 0;

      .second {
        margin-left: 4px;
      }
    }
  }
}
</style>
